import { useEffect, useRef } from 'react';
import { HashRouter, Outlet, Route, Routes, useLocation, useMatch } from 'react-router';
import { RoomDialogsProvider } from './rooms/RoomDialogs';
import { Rail } from './rooms/Rail';
import { Home } from './rooms/Home';
import { Board } from './board/Board';
import { ItemPanel } from './item/Panel';
import { Settings } from './settings/Settings';
import { SearchOverlay } from './search/SearchOverlay';
import { EmptyState } from './ui/EmptyState';
import { useAppKeys } from './useAppKeys';
import './App.css';

/**
 * A hash router: a file-served Tauri app and a real web server disagree about
 * path routing, and the hash sidesteps it entirely.
 *
 *   #/                                home — all rooms
 *   #/room/:roomId                    board
 *   #/room/:roomId/:itemId            board with the panel open on that item
 *   #/room/:roomId/s/:sectionId       board, inside a section
 *   #/room/:roomId/s/:sectionId/:itemId
 *   #/settings
 *
 * Only the section you're in is in the address; its ancestors are read from
 * the record, so nesting doesn't lengthen the URL.
 */
export function App() {
  return (
    <HashRouter>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Home />} />
          <Route path="room/:roomId" element={<Board />} />
          <Route path="room/:roomId/s/:sectionId" element={<Board />} />
          <Route path="room/:roomId/s/:sectionId/:itemId" element={<Board />} />
          <Route path="room/:roomId/:itemId" element={<Board />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </HashRouter>
  );
}

function Shell() {
  // The panel is driven by the URL, not component state — that's what makes
  // back/forward work through a run of items.
  useAppKeys();

  const plain = useMatch('/room/:roomId/:itemId');
  const inSection = useMatch('/room/:roomId/s/:sectionId/:itemId');
  const match = inSection ?? plain;

  const roomId = match?.params.roomId;
  const itemId = match?.params.itemId;
  // Closing the panel, and arrowing between items, has to come back to the
  // container you opened it from rather than to the top of the room.
  const base =
    inSection === null
      ? `/room/${roomId}`
      : `/room/${roomId}/s/${inSection.params.sectionId}`;

  const main = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  /**
   * Every screen shares one scroll box, and nothing used to put it back to the
   * top. So each screen opened wherever the last one had been left, clamped to
   * its own height: scroll a long way down a room, tap Home, and Home opened
   * at its very bottom. Trying to scroll down from there does nothing, which
   * reads as the scroll having locked up rather than as it being already
   * finished.
   *
   * Keyed on the screen rather than on the URL, because the item panel is a
   * route too. Opening an item is not arriving somewhere new — the board is
   * still behind it and you expect your place on it back when you close — so
   * `base` deliberately drops the item id and `/room/x/item1` counts as the
   * same screen as `/room/x`.
   */
  const screen = match === null ? pathname : base;

  useEffect(() => {
    // `auto`, and unconditionally: this doubles as the thing that stops a
    // smooth scroll still animating from the screen being left behind.
    main.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [screen]);

  return (
    // The room dialogs are owned here: the rail, the home grid and a board
    // header all open the same ones.
    <RoomDialogsProvider>
      <div className="shell">
        <Rail />
        <main className="shell__main" ref={main}>
          {/* Inside the scrolling column and above it: the board behind it is
              what it filters, so it takes a strip of the page rather than
              floating over the heading it would otherwise cover. */}
          <SearchOverlay />
          <Outlet />
        </main>
        {roomId !== undefined && itemId !== undefined && (
          <ItemPanel base={base} itemId={itemId} />
        )}
      </div>
    </RoomDialogsProvider>
  );
}

function NotFound() {
  return (
    <div className="shell__not-found">
      <EmptyState title="Nothing Here" line="That address doesn't match a room or a screen." />
    </div>
  );
}
